import { useEffect, useState, useCallback } from "react";
import { Radio, Plug, CheckCircle, AlertTriangle } from "lucide-react";
import { theme, card, inputStyle, btnPrimary } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";

interface SiemStatus {
  enabled:         boolean;
  endpointUrl:     string | null;
  authHeaderName:  string | null;
  connectedAt:     string | null;
  lastForwardedAt: string | null;
  lastError:       string | null;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

const HEADER_PRESETS = [
  { label: "Authorization (Bearer / API key)",   value: "Authorization" },
  { label: "X-Splunk-HEC-Token (Splunk HEC)",    value: "X-Splunk-HEC-Token" },
  { label: "X-Api-Key (Elastic, Datadog, etc.)", value: "X-Api-Key" },
  { label: "Custom…",                             value: "" },
];

export function ConnectSiemPage() {
  const [status,      setStatus]      = useState<SiemStatus | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [testing,     setTesting]     = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [success,     setSuccess]     = useState<string | null>(null);
  const [showForm,    setShowForm]    = useState(false);
  const [headerPreset, setHeaderPreset] = useState(HEADER_PRESETS[0].value);

  const [form, setForm] = useState({
    endpointUrl:     "",
    authHeaderName:  "Authorization",
    authHeaderValue: "",
  });

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.get<SiemStatus>("/siem/status");
      setStatus(data);
    } catch {
      setError("Failed to load SIEM status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  function handlePresetChange(preset: string) {
    setHeaderPreset(preset);
    if (preset !== "") setForm((f) => ({ ...f, authHeaderName: preset }));
  }

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.post("/siem/configure", form);
      setShowForm(false);
      setForm({ endpointUrl: "", authHeaderName: "Authorization", authHeaderValue: "" });
      setSuccess("SIEM forwarding enabled. A test event was sent successfully.");
      await loadStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Connection failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      await api.post("/siem/test", {});
      setSuccess("Test event delivered successfully.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Test delivery failed");
    } finally {
      setTesting(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Disable SIEM forwarding and clear all configuration?")) return;
    setDisconnecting(true);
    setError(null);
    setSuccess(null);
    try {
      await api.del("/siem/configure");
      setSuccess(null);
      await loadStatus();
    } catch {
      setError("Disconnect failed");
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) return <LoadingBlock label="Loading SIEM status…" />;
  if (!status) return <div style={{ color: "var(--c-dangerText)", padding: "20px" }}>{error || "Failed to load status"}</div>;
  const s = status;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <Radio size={22} color="var(--c-accentText)" />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.color.heading }}>
              SIEM Forwarding
            </h2>
            {s.enabled && (
              <span style={{ background: "var(--c-successBg)", color: "var(--c-successText)", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 3 }}>
                Active
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Forward audit log entries to your SIEM as CEF events over HTTPS. Works with any SIEM
            that accepts HTTP — Splunk HEC, Elastic, IBM QRadar, Microsoft Sentinel, Datadog, Sumo Logic, and more.
          </p>
        </div>
        {s.enabled && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button onClick={handleTest} disabled={testing}
              style={{ ...btnPrimary, padding: "7px 14px", fontSize: 13, opacity: testing ? 0.6 : 1, cursor: testing ? "not-allowed" : "pointer" }}>
              {testing ? "Sending…" : "Send Test Event"}
            </button>
            <button onClick={handleDisconnect} disabled={disconnecting}
              style={{ padding: "7px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: "var(--c-dangerText)", cursor: "pointer", opacity: disconnecting ? 0.6 : 1 }}>
              Disconnect
            </button>
          </div>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-successBg)", border: "1px solid var(--c-success)", color: "var(--c-successText)", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle size={14} /> {success}
        </div>
      )}

      {/* Not connected */}
      {!s.enabled && !showForm && (
        <div style={{ ...card, textAlign: "center", padding: "40px 28px" }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Plug size={36} color="var(--c-muted)" /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
            Connect your SIEM
          </div>
          <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 540, margin: "0 auto 20px", lineHeight: 1.6 }}>
            Audit log events are forwarded in <strong>CEF (Common Event Format)</strong> over HTTPS,
            in a JSON envelope that includes both the CEF string and structured fields. Point it at any
            SIEM HTTP listener — no vendor-specific configuration required on our side.
          </p>

          {/* Protocol info */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, maxWidth: 580, margin: "0 auto 24px", textAlign: "left" }}>
            {[
              { title: "Splunk HEC",       desc: "Use the HEC token as the auth header value" },
              { title: "Elastic / Logstash", desc: "Point at the HTTP input plugin URL" },
              { title: "Any SIEM",         desc: "Configure an HTTP listener, paste the URL here" },
            ].map(({ title, desc }) => (
              <div key={title} style={{ ...card, padding: "12px 14px", background: "var(--c-faint)", border: "1px solid var(--c-border)" }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: theme.color.heading, marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 11, color: theme.color.muted, lineHeight: 1.5 }}>{desc}</div>
              </div>
            ))}
          </div>

          <button onClick={() => setShowForm(true)} style={{ ...btnPrimary, padding: "9px 22px", fontSize: 13, cursor: "pointer" }}>
            Configure SIEM Endpoint
          </button>
        </div>
      )}

      {/* Connect form */}
      {!s.enabled && showForm && (
        <div style={{ ...card }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: theme.color.heading }}>
            SIEM Endpoint Configuration
          </h3>
          <p style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 18px", lineHeight: 1.6 }}>
            A test event will be delivered to verify the endpoint before saving. Events are sent every
            60 seconds. Each delivery is a JSON POST containing CEF-formatted events and structured fields.
          </p>

          <form onSubmit={handleConnect} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>SIEM Endpoint URL</span>
              <input
                type="url"
                value={form.endpointUrl}
                onChange={(e) => setForm((f) => ({ ...f, endpointUrl: e.target.value }))}
                placeholder="https://your-siem.example.com/collector/events"
                required
                style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }}
              />
              <span style={{ fontSize: 11, color: theme.color.muted }}>Must be HTTPS. For Splunk HEC: https://splunk-host:8088/services/collector/event</span>
            </label>

            <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>Auth Header</span>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <span style={{ fontSize: 11, color: theme.color.muted }}>Header name</span>
                  <select
                    value={headerPreset}
                    onChange={(e) => handlePresetChange(e.target.value)}
                    style={{ ...inputStyle, fontSize: 13 }}
                  >
                    {HEADER_PRESETS.map((p) => (
                      <option key={p.label} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  {headerPreset === "" && (
                    <input
                      type="text"
                      value={form.authHeaderName}
                      onChange={(e) => setForm((f) => ({ ...f, authHeaderName: e.target.value }))}
                      placeholder="X-Custom-Header"
                      required
                      style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }}
                    />
                  )}
                </div>
                <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <span style={{ fontSize: 11, color: theme.color.muted }}>Header value (token / API key)</span>
                  <input
                    type="password"
                    value={form.authHeaderValue}
                    onChange={(e) => setForm((f) => ({ ...f, authHeaderValue: e.target.value }))}
                    placeholder="your-token-or-api-key"
                    required
                    style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }}
                  />
                </label>
              </div>
              <span style={{ fontSize: 11, color: theme.color.muted }}>
                Stored encrypted at rest. The header is sent with every delivery request — e.g. <code>Authorization: Bearer eyJ…</code>
              </span>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button type="submit" disabled={saving}
                style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Validating & Connecting…" : "Connect"}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Connected — status card */}
      {s.enabled && (
        <>
          <div style={{ ...card, padding: "16px 20px", marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, fontSize: 13 }}>
              <div>
                <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 2 }}>Endpoint</div>
                <div style={{ fontFamily: "monospace", fontSize: 12, color: theme.color.text, wordBreak: "break-all" }}>
                  {s.endpointUrl}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 2 }}>Auth header</div>
                <div style={{ fontFamily: "monospace", fontSize: 12, color: theme.color.text }}>
                  {s.authHeaderName}: ••••••••
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 2 }}>Connected</div>
                <div style={{ fontSize: 13, color: theme.color.text }}>{fmtDate(s.connectedAt)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 2 }}>Last delivery</div>
                <div style={{ fontSize: 13, color: theme.color.text }}>{fmtDate(s.lastForwardedAt)}</div>
              </div>
            </div>
          </div>

          {/* Last error */}
          {s.lastError && (
            <div style={{ ...card, padding: "10px 14px", marginBottom: 16, background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <strong>Last delivery failed:</strong> {s.lastError}
                <div style={{ fontSize: 11, marginTop: 4 }}>Events will retry automatically on the next cycle (every 60 seconds).</div>
              </div>
            </div>
          )}

          {/* Format reference */}
          <div style={{ ...card, padding: "14px 18px" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 10 }}>
              Event format reference
            </div>
            <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 8, lineHeight: 1.6 }}>
              Each delivery is a single HTTPS POST with <code>Content-Type: application/json</code>.
              Events are batched (up to 100 per request). The body contains a <code>cef</code> string
              and structured fields — your SIEM can use whichever it prefers.
            </div>
            <pre style={{
              background: "var(--c-faint)", border: "1px solid var(--c-border)",
              borderRadius: 3, padding: "10px 12px", fontSize: 11, overflowX: "auto",
              color: theme.color.text, margin: 0, lineHeight: 1.6,
            }}>
{`{
  "source": "abacas-security-compliance-platform",
  "eventCount": 1,
  "events": [{
    "cef": "CEF:0|Abacas Security|Compliance Platform|1.0|CONTROL_UPDATED|Control Updated|5|rt=1234567890000 cat=review cs1=client cs1Label=Role cs2=orgId cs2Label=OrganizationId msg=Updated control A.5.1",
    "id": "clxyz…",
    "timestamp": "2026-08-13T10:30:00.000Z",
    "category": "review",
    "action": "CONTROL_UPDATED",
    "role": "client",
    "userId": "usr_…",
    "detail": "Updated control A.5.1",
    "organizationId": "org_…"
  }]
}`}
            </pre>
          </div>
        </>
      )}
    </div>
  );
}
